import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useState } from 'react';

import { MeasureSolutionResponseItem } from '@e-origin/contracts';

import { TextInput } from '@components';
import { EditMeasureSolutionValues, useEditMeasureSolutionFormInitializer } from './useEditMeasureSolutionFormInitializer';

interface EditMeasureSolutionProps {
  measureSolution?: MeasureSolutionResponseItem;
  onHide: () => void;
}

export const EditMeasureSolution: React.FC<EditMeasureSolutionProps> = ({ measureSolution, onHide }) => {
  const [formError, setFormError] = useState<string | null>(null);
  const { form, onSubmit, isSubmitting } = useEditMeasureSolutionFormInitializer(measureSolution);

  const uppercaseInputProps = (field: 'origin' | 'group' | 'solution') => ({
    ...form.getInputProps(field),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue(field, event.currentTarget.value.toUpperCase()),
  });

  const handleSubmit = async (values: EditMeasureSolutionValues) => {
    setFormError(null);

    try {
      await onSubmit(values);
      onHide();
    } catch (error) {
      setFormError((error as Error)?.message || 'Failed to save measure solution');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" my="xl">
        <TextInput {...form.getInputProps('hsCode')} label="HS code" placeholder="e.g. 9705220016" />
        <TextInput {...uppercaseInputProps('origin')} label="Origin" placeholder="e.g. CN" />
        <TextInput {...form.getInputProps('measure')} label="Measure type" placeholder="e.g. 755" />
        <TextInput {...uppercaseInputProps('group')} label="Condition group" placeholder="e.g. E" />
        <TextInput {...uppercaseInputProps('solution')} label="Solution" placeholder="e.g. Y923, C126 or 20.000/KGM" />
        <Text size="xs" c="dimmed">
          Only one solution can be set for each HS code, origin, measure type and condition group.
        </Text>
      </Stack>

      {formError && (
        <Text c="red" fw="bold" mt="md">
          {formError}
        </Text>
      )}

      <Group justify="center" mt="xl">
        <Button type="button" onClick={onHide} disabled={isSubmitting} size="lg" variant="transparent">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!form.isValid() || isSubmitting}
          leftSection={isSubmitting && <Loader color="white" size="xs" />}
          variant="filled"
          size="lg"
        >
          Save
        </Button>
      </Group>
    </form>
  );
};
